import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { LegendComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { FaBuilding, FaChevronRight, FaExclamationTriangle, FaExternalLinkAlt } from 'react-icons/fa';
import { Card, Badge, CheckboxMultiSelect } from 'components/common';

echarts.use([PieChart, TooltipComponent, LegendComponent, CanvasRenderer]);

// Site-office multi-select filter — thin preset over the reusable CheckboxMultiSelect.
export const SiteFilter = ({ sites = [], value = [], onChange }) => (
  <CheckboxMultiSelect options={sites} value={value} onChange={onChange} icon={FaBuilding} allLabel="All sites" />
);
SiteFilter.propTypes = { sites: PropTypes.array, value: PropTypes.array, onChange: PropTypes.func };

// Generic detail table with an optional link column (booking → detail page in new tab).
export const BookingTable = ({ title, rows = [], columns = [], linkBase, linkKey = 'id', linkCol, emptyText = 'No data' }) => (
  <Card padding="none" header={<h6 className="tw-mb-0 tw-font-semibold tw-text-slate-700">{title}</h6>}>
    <div className="tw-max-h-80 tw-overflow-auto">
      <table className="tw-w-full tw-text-xs tw-border-collapse">
        <thead className="tw-sticky tw-top-0 tw-bg-slate-100">
          <tr className="tw-text-left">
            {columns.map((c) => <th key={c.key} className={`tw-px-2 tw-py-2 ${c.align === 'right' ? 'tw-text-right' : ''}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} className="tw-text-center tw-text-slate-400 tw-py-4">{emptyText}</td></tr>}
          {rows.map((row, ri) => (
            <tr key={row.id ?? ri} className="tw-border-b tw-border-slate-100 hover:tw-bg-slate-50">
              {columns.map((c) => {
                const raw = c.render ? c.render(row) : row[c.key];
                const isLink = linkBase && c.key === linkCol;
                return (
                  <td key={c.key} className={`tw-px-2 tw-py-1.5 ${c.align === 'right' ? 'tw-text-right' : ''}`}>
                    {isLink
                      ? <Link to={`${linkBase}/${row[linkKey]}`} target="_blank" className="tw-text-blue-600 hover:tw-underline tw-inline-flex tw-items-center tw-gap-1">{raw} <FaExternalLinkAlt size={8} className="tw-opacity-60" /></Link>
                      : raw}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);
BookingTable.propTypes = { title: PropTypes.string, rows: PropTypes.array, columns: PropTypes.array, linkBase: PropTypes.string, linkKey: PropTypes.string, linkCol: PropTypes.string, emptyText: PropTypes.string };

const BAR_COLORS = ['#6366f1', '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#14b8a6', '#ec4899'];

// Doughnut distribution chart (ECharts). Click a slice → drill-down, same as BarList.
export const DonutChart = ({ title, items = [], onItemClick, emptyText = 'No data' }) => {
  const option = {
    tooltip: { trigger: 'item', formatter: (p) => `${p.name}: <b>${p.value}</b> (${p.percent}%)` },
    legend: { type: 'scroll', orient: 'vertical', right: 6, top: 'middle', icon: 'circle', textStyle: { color: '#64748b', fontSize: 11 } },
    series: [{
      type: 'pie',
      radius: ['48%', '72%'],
      center: ['33%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: { borderColor: '#fff', borderWidth: 2 },
      label: { show: false },
      labelLine: { show: false },
      data: items.map((i, idx) => ({ value: i.value, name: i.label, itemStyle: { color: BAR_COLORS[idx % BAR_COLORS.length] } })),
    }],
  };
  const onEvents = { click: (p) => { if (onItemClick && p.dataIndex != null) onItemClick(items[p.dataIndex]); } };
  return (
    <Card padding="none" header={<h6 className="tw-mb-0 tw-font-semibold tw-text-slate-700">{title}</h6>}>
      <div className="tw-p-3" style={{ height: 240 }}>
        {items.length === 0
          ? <div className="tw-text-center tw-text-slate-400 tw-py-10">{emptyText}</div>
          : <ReactEChartsCore echarts={echarts} option={option} onEvents={onEvents} style={{ height: '100%', width: '100%' }} notMerge lazyUpdate />}
      </div>
    </Card>
  );
};
DonutChart.propTypes = { title: PropTypes.string, items: PropTypes.array, onItemClick: PropTypes.func, emptyText: PropTypes.string };

export const StatTile = ({ label, value, icon: Icon, accent = 'tw-text-blue-600', bg = 'tw-bg-blue-50', delta = null, highlight = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    title={onClick ? 'View details' : undefined}
    className={`tw-w-full tw-text-left tw-flex tw-items-center tw-gap-3 tw-border tw-border-solid tw-rounded-lg tw-p-4 tw-shadow-sm tw-transition-shadow ${highlight ? 'tw-bg-red-50 tw-border-red-200' : 'tw-bg-white tw-border-slate-200'} ${onClick ? 'tw-cursor-pointer hover:tw-shadow-md hover:tw-border-blue-300' : 'tw-cursor-default'}`}
  >
    <div className={`tw-w-11 tw-h-11 tw-rounded tw-flex tw-items-center tw-justify-center ${bg}`}>{Icon ? <Icon className={accent} size={20} /> : null}</div>
    <div className="tw-min-w-0">
      <div className="tw-text-2xl tw-font-bold tw-text-slate-800 tw-leading-none tw-flex tw-items-baseline tw-gap-2">
        {(value ?? 0).toLocaleString()}
        {delta !== null && delta !== undefined && (
          <span className={`tw-text-xs tw-font-semibold ${delta >= 0 ? 'tw-text-green-600' : 'tw-text-red-600'}`}>{delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}%</span>
        )}
      </div>
      <div className="tw-text-xs tw-text-slate-500 tw-mt-1 tw-flex tw-items-center tw-gap-1">{label}{onClick && <FaChevronRight size={8} className="tw-text-slate-300" />}</div>
    </div>
  </button>
);
StatTile.propTypes = { label: PropTypes.string, value: PropTypes.number, icon: PropTypes.elementType, accent: PropTypes.string, bg: PropTypes.string, delta: PropTypes.number, highlight: PropTypes.bool, onClick: PropTypes.func };

// Clickable horizontal bar list. onItemClick → drill-down. renderMeta → extra metrics.
export const BarList = ({ title, items = [], emptyText = 'No data', onItemClick, renderMeta }) => {
  const max = Math.max(1, ...items.map((i) => i.value || 0));
  return (
    <Card padding="none" header={<h6 className="tw-mb-0 tw-font-semibold tw-text-slate-700">{title}</h6>}>
      <div className="tw-p-4 tw-flex tw-flex-col tw-gap-2.5">
        {items.length === 0 && <div className="tw-text-sm tw-text-slate-400 tw-py-4 tw-text-center">{emptyText}</div>}
        {items.map((item, i) => (
          <button
            key={item.label + i}
            type="button"
            onClick={onItemClick ? () => onItemClick(item) : undefined}
            className={`tw-w-full tw-text-left tw-bg-transparent tw-border-0 tw-p-0 ${onItemClick ? 'tw-cursor-pointer hover:tw-opacity-80' : 'tw-cursor-default'}`}
          >
            <div className="tw-flex tw-justify-between tw-text-xs tw-mb-1">
              <span className="tw-text-slate-600 tw-truncate tw-pr-2 tw-flex tw-items-center tw-gap-1">
                {item.label}{onItemClick && <FaChevronRight size={8} className="tw-text-slate-300" />}
              </span>
              <span className="tw-font-semibold tw-text-slate-800">{item.value}{renderMeta ? <span className="tw-text-slate-400 tw-font-normal">{renderMeta(item)}</span> : null}</span>
            </div>
            <div className="tw-h-2 tw-w-full tw-bg-slate-100 tw-rounded-xl tw-overflow-hidden" title={`${item.label}: ${item.value}`}>
              <div className="tw-h-full tw-rounded-xl" style={{ width: `${((item.value || 0) / max) * 100}%`, backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }} />
            </div>
          </button>
        ))}
      </div>
    </Card>
  );
};
BarList.propTypes = { title: PropTypes.string, items: PropTypes.array, emptyText: PropTypes.string, onItemClick: PropTypes.func, renderMeta: PropTypes.func };

export const MonthlyTrend = ({ title, items = [] }) => {
  const max = Math.max(1, ...items.map((i) => i.value || 0));
  return (
    <Card padding="none" header={<h6 className="tw-mb-0 tw-font-semibold tw-text-slate-700">{title}</h6>}>
      <div className="tw-p-4 tw-flex tw-items-end tw-justify-between tw-gap-2" style={{ height: 180 }}>
        {items.map((item, i) => (
          <div key={item.label + i} className="tw-flex tw-flex-col tw-items-center tw-flex-1 tw-h-full tw-justify-end" title={`${item.label}: ${item.value}`}>
            <span className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-mb-1">{item.value}</span>
            <div className="tw-w-full tw-rounded-t-md tw-bg-primary-500" style={{ height: `${((item.value || 0) / max) * 100}%`, minHeight: item.value ? 4 : 0 }} />
            <span className="tw-text-xs tw-text-slate-400 tw-mt-1">{item.label}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};
MonthlyTrend.propTypes = { title: PropTypes.string, items: PropTypes.array };

// Year-over-year grouped bars (this year vs last year, per month).
export const YoYChart = ({ title, yoy = {} }) => {
  const months = yoy.months || [];
  const max = Math.max(1, ...months.flatMap((m) => [m.this || 0, m.last || 0]));
  const [hover, setHover] = React.useState(null);
  return (
    <Card padding="none" header={(
      <div className="tw-flex tw-items-center tw-justify-between">
        <h6 className="tw-mb-0 tw-font-semibold tw-text-slate-700">{title}</h6>
        <div className="tw-flex tw-items-center tw-gap-3 tw-text-xs">
          <span className="tw-flex tw-items-center tw-gap-1"><span className="tw-w-3 tw-h-3 tw-rounded-sm tw-bg-primary-500" /> This year <strong>{yoy.this_year ?? 0}</strong></span>
          <span className="tw-flex tw-items-center tw-gap-1"><span className="tw-w-3 tw-h-3 tw-rounded-sm tw-bg-slate-300" /> Last year <strong>{yoy.last_year ?? 0}</strong></span>
          {yoy.delta_pct !== null && yoy.delta_pct !== undefined && (
            <span className={`tw-font-semibold ${yoy.delta_pct >= 0 ? 'tw-text-green-600' : 'tw-text-red-600'}`}>{yoy.delta_pct >= 0 ? '▲' : '▼'} {Math.abs(yoy.delta_pct)}%</span>
          )}
        </div>
      </div>
    )}>
      <div className="tw-p-4 tw-flex tw-items-end tw-justify-between tw-gap-1.5" style={{ height: 200 }}>
        {months.map((m, i) => {
          const active = hover === i;
          const delta = (m.this || 0) - (m.last || 0);
          return (
            <div
              key={m.label + i}
              className="tw-relative tw-flex tw-flex-col tw-items-center tw-flex-1 tw-h-full tw-justify-end tw-cursor-default"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover((h) => (h === i ? null : h))}
            >
              {active && (
                <div className="tw-absolute tw-bottom-full tw-left-1/2 -tw-translate-x-1/2 tw-mb-2 tw-z-20 tw-whitespace-nowrap tw-rounded tw-px-3 tw-py-2 tw-text-[11px] tw-text-white tw-shadow-lg" style={{ backgroundColor: '#1e293b' }}>
                  <div className="tw-font-semibold tw-mb-1">{m.label}</div>
                  <div className="tw-flex tw-items-center tw-gap-1.5"><span className="tw-w-2 tw-h-2 tw-rounded-sm tw-bg-primary-400" /> This year: <strong>{m.this || 0}</strong></div>
                  <div className="tw-flex tw-items-center tw-gap-1.5"><span className="tw-w-2 tw-h-2 tw-rounded-sm tw-bg-slate-400" /> Last year: <strong>{m.last || 0}</strong></div>
                  <div className={`tw-mt-1 tw-font-semibold ${delta >= 0 ? 'tw-text-green-400' : 'tw-text-red-400'}`}>{delta >= 0 ? '▲ +' : '▼ '}{delta}</div>
                  <div className="tw-absolute tw-top-full tw-left-1/2 -tw-translate-x-1/2 tw-w-0 tw-h-0" style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #1e293b' }} />
                </div>
              )}
              <div className={`tw-flex tw-items-end tw-gap-0.5 tw-w-full tw-justify-center tw-h-full tw-rounded-sm ${active ? 'tw-bg-slate-50' : ''}`}>
                <div className="tw-w-1/2 tw-rounded-t-sm tw-bg-primary-500 tw-transition-opacity" style={{ height: `${((m.this || 0) / max) * 100}%`, minHeight: m.this ? 3 : 0, opacity: hover === null || active ? 1 : 0.55 }} />
                <div className="tw-w-1/2 tw-rounded-t-sm tw-bg-slate-300 tw-transition-opacity" style={{ height: `${((m.last || 0) / max) * 100}%`, minHeight: m.last ? 3 : 0, opacity: hover === null || active ? 1 : 0.55 }} />
              </div>
              <span className={`tw-text-[10px] tw-mt-1 ${active ? 'tw-text-slate-700 tw-font-semibold' : 'tw-text-slate-400'}`}>{m.label}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
YoYChart.propTypes = { title: PropTypes.string, yoy: PropTypes.object };

// A list of booking "brief" items with status + optional urgency/aging badges.
export const BookingList = ({ title, items = [], dateField = 'date_of_travel', metaField = 'destination', emptyText = 'No records', variant = 'default', icon: Icon, linkBase }) => {
  const tone = variant === 'urgent' ? 'tw-bg-red-50 tw-border-red-200' : variant === 'highlight' ? 'tw-bg-amber-50 tw-border-amber-200' : '';
  return (
    <Card padding="none" className={tone} header={(
      <h6 className="tw-mb-0 tw-font-semibold tw-text-slate-700 tw-flex tw-items-center tw-gap-2">
        {Icon && <Icon className={variant === 'urgent' ? 'tw-text-red-500' : 'tw-text-amber-500'} />}
        {title}
        {items.length > 0 && <span className="tw-text-xs tw-font-normal tw-text-slate-400">({items.length})</span>}
      </h6>
    )}>
      <div className="tw-max-h-72 tw-overflow-y-auto">
        {items.length === 0 && <div className="tw-text-sm tw-text-slate-400 tw-py-6 tw-text-center">{emptyText}</div>}
        {items.map((b) => (
          <div key={b.id} className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-border-b tw-border-slate-100 tw-text-sm">
            <div className="tw-min-w-0">
              {linkBase ? (
                <Link to={`${linkBase}/${b.id}`} target="_blank" className="tw-font-medium tw-text-blue-600 hover:tw-underline tw-truncate tw-flex tw-items-center tw-gap-1">
                  {b.title} <FaExternalLinkAlt size={9} className="tw-flex-shrink-0 tw-opacity-60" />
                </Link>
              ) : (
                <div className="tw-font-medium tw-text-slate-700 tw-truncate">{b.title}</div>
              )}
              <div className="tw-text-xs tw-text-slate-400 tw-truncate">{b.booking_number}{b[metaField] ? ` · ${b[metaField]}` : ''}</div>
              {/* Job / project / customer context — helps identify trips whose title is
                  generic (e.g. "FACTORY VISIT"). Only shows the parts that exist. */}
              {(b.job_number || b.project_name || b.customer_name) && (
                <div className="tw-text-[11px] tw-text-slate-500 tw-truncate tw-mt-0.5 tw-flex tw-items-center tw-gap-1.5">
                  {b.job_number && <span className="tw-font-semibold tw-text-primary-600">{b.job_number}</span>}
                  {b.project_name && <span className="tw-truncate">{b.project_name}</span>}
                  {b.customer_name && <span className="tw-text-slate-400 tw-truncate">· {b.customer_name}</span>}
                </div>
              )}
            </div>
            <div className="tw-text-right tw-flex-shrink-0 tw-pl-2 tw-flex tw-flex-col tw-items-end tw-gap-1">
              <div className="tw-text-xs tw-text-slate-600">{b[dateField] ? dayjs(b[dateField]).format('DD/MM/YYYY') : ''}</div>
              <div className="tw-flex tw-items-center tw-gap-1">
                {b.missed && <Badge variant="danger" size="sm"><FaExclamationTriangle size={9} /> Missed</Badge>}
                {typeof b.days_until === 'number' && !b.missed && <Badge variant={b.days_until <= 1 ? 'danger' : 'warning'} size="sm">{b.days_until <= 0 ? 'Today' : `${b.days_until}d`}</Badge>}
                {typeof b.days_pending === 'number' && <Badge variant="warning" size="sm">{b.days_pending}d pending</Badge>}
                {b.status_name && <Badge variant={b.pending ? 'warning' : 'info'} size="sm">{b.status_name}</Badge>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
BookingList.propTypes = {
  title: PropTypes.string, items: PropTypes.array, dateField: PropTypes.string, metaField: PropTypes.string,
  emptyText: PropTypes.string, variant: PropTypes.oneOf(['default', 'urgent', 'highlight']), icon: PropTypes.elementType,
  linkBase: PropTypes.string,
};
